import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIGS

// COMPONENTS
import { PackageComponent } from '../../../package/components/package/package.component';

// INTEFACES
import { IPackage } from '../../../package/interfaces/package.interface';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { IPaginationResponse } from '../../../shared/interfaces/paginator.interface';

// SERVICES
import { PackageApiService } from '../../../shared/services/api/package/package-api.service';

// UTILS
import { trackById } from '../../../shared/util/track-by.util';

import { LoadingDialogUtilService } from './../../../shared/services/util/loading-dialog-util.service';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

@Component({
  selector: 'lum-invoice-package-dropdown',
  templateUrl: './invoice-package-dropdown.component.html',
  styleUrls: ['./invoice-package-dropdown.component.scss'],
})
export class InvoicePackageDropdownComponent implements OnInit, OnDestroy {
  @Input()
  isInvoiceStatusCompleteSalesOrderStatusShipped :boolean = false;
  @Output()
  applyPackage :EventEmitter<IPackage> = new EventEmitter<IPackage>();

  packages$ = new BehaviorSubject<IPackage[]>([]);

  private onDestroy$ = new Subject();
  private loading$ = new BehaviorSubject<boolean>(false);

  trackById = trackById;

  constructor(
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private packageApiService :PackageApiService,
    private loadingDialogService :LoadingDialogUtilService,
  ) {}

  ngOnInit() :void {
    this.getPackages();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  togglePackageMenu(event :MouseEvent) :void {
    event.stopPropagation();
  }

  onSelectPackage(data :IPackage) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Apply Invoice Template',
          message: 'This action will apply the selected invoice template. Proceed?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Applying invoice template...');

          this.packageApiService
            .getById(data.id, {
              relations: 'packageItems',
            })
            .pipe(
              finalize(() => this.loadingDialogService.close()),
              takeUntil(this.onDestroy$),
            )
            .subscribe({
              next: (response :any) => {
                this.applyPackage.emit(response);
              },
              error: (error) => {
                this.snackBar.open('Error getting invoice template', 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
            });
        }
      });
  }

  onManagePackage() :void {
    this.dialog
      .open(PackageComponent, {
        width: DialogConfig.sizes.large,
        maxHeight: 'calc(100vh - 150px)',
        data: {
          packages: this.packages,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        this.getPackages();
      });
  }

  addPackageToDropdown(data :IPackage) :void {
    const currentPackages = this.packages$.getValue();
    currentPackages.push(data);
    this.packages$.next(currentPackages);
  }

  private getPackages() :void {
    this.loading$.next(true);
    this.packageApiService
      .get({
        limit: '999',
      })
      .pipe(
        finalize(() => this.loading$.next(false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: (response :IPaginationResponse) => {
          this.packages$.next(response.data);
        },
        error: (error) => {
          this.snackBar.open('Error getting invoice templates', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get loading() :boolean {
    return this.loading$.getValue();
  }

  get packages() :IPackage[] {
    return this.packages$.getValue();
  }
}
